import { useEffect, useRef, useState } from "react";
import { useClerk, useOrganization, useSession, useUser } from "@clerk/clerk-react";

type Failure = "load" | "activation";
type MembershipListState = {
  isLoading: boolean;
  count?: number;
  defaultOrganizationId?: string;
  error: unknown;
  revalidate?: () => Promise<unknown>;
};

export function useOrganizationInitialization({
  isLoading, count, defaultOrganizationId, error, revalidate,
}: MembershipListState) {
  const clerk = useClerk();
  const { user } = useUser();
  const { session } = useSession();
  const { organization } = useOrganization();
  const userId = user?.id;
  const sessionId = session?.id;
  const sessionKey = `${userId}:${sessionId}`;
  const [failure, setFailure] = useState<{ sessionKey: string; error: Failure }>();
  const [retryRequest, setRetryRequest] = useState<{ sessionKey: string }>();
  const retry = retryRequest?.sessionKey === sessionKey ? retryRequest : undefined;
  // Clerk returns a new callback per render; use the latest without restarting recovery.
  const revalidateRef = useRef(revalidate);
  revalidateRef.current = revalidate;
  const work = useRef<{
    sessionKey: string;
    retry?: typeof retry;
    reload?: Promise<boolean>;
    activation?: Promise<boolean>;
  }>();
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!clerk.loaded || !userId || !sessionId) return;
    let cancelled = false;
    const sameSession = () => mounted.current && clerk.loaded && clerk.user?.id === userId &&
      clerk.session?.id === sessionId && clerk.session.user.id === userId;
    const isCurrent = () => !cancelled && sameSession();
    const isMember = (id: string) => !!clerk.user?.organizationMemberships.some(m => m.organization.id === id);
    const cacheBroken = () => {
      const id = clerk.organization?.id ?? clerk.session?.lastActiveOrganizationId;
      if (id) return !isMember(id);
      const hasMemberships = !!clerk.user?.organizationMemberships.length;
      return ((count ?? 0) > 0 && !hasMemberships) ||
        (!isLoading && !error && count === 0 && hasMemberships);
    };
    const report = (error?: Failure) => {
      if (!isCurrent()) return;
      setFailure(previous => error
        ? previous?.sessionKey === sessionKey && previous.error === error ? previous : { sessionKey, error }
        : undefined);
    };

    const initialize = async () => {
      const previous = work.current;
      // Keep pending work across renders, including SDK updates published before a request settles.
      await Promise.all([previous?.reload, previous?.activation]);
      if (!isCurrent()) return;
      if (previous?.sessionKey !== sessionKey || previous.retry !== retry) work.current = { sessionKey, retry };
      const current = work.current!;
      let refreshed = true;
      if (cacheBroken() || retry) {
        current.reload ??= (async () => {
          try {
            await clerk.user!.reload();
            if (sameSession()) await revalidateRef.current?.();
            return true;
          } catch { return false; }
        })();
        refreshed = await current.reload;
        if (!isCurrent()) return;
      }

      // Use live SDK data after the request; its return value may be an older resource.
      if (clerk.organization?.id) {
        if (!isMember(clerk.organization.id)) { report("load"); return; }
        report();
        work.current = { sessionKey };
        setRetryRequest(undefined);
        return;
      }
      const savedId = clerk.session!.lastActiveOrganizationId;
      // A confirmed saved membership does not depend on the separate list query recovering.
      let target: string | null | undefined = savedId && isMember(savedId) ? savedId : undefined;
      if (!target) {
        if (!refreshed || error) { report("load"); return; }
        if (isLoading || count === undefined) return;
        if (!count) {
          // Prefer refreshed memberships when the separate list remains empty.
          target = clerk.user!.organizationMemberships[0]?.organization.id;
          // Clerk clears the saved selection for null; Navbar then handles onboarding.
          if (!target && savedId) target = null;
          if (target === undefined) {
            report();
            work.current = { sessionKey };
            setRetryRequest(undefined);
            return;
          }
        } else target = defaultOrganizationId;
      }
      if (target === undefined || (target !== null && !isMember(target))) { report("load"); return; }
      current.activation ??= clerk.setActive({ organization: target }).then(
        () => target !== null || !clerk.session?.lastActiveOrganizationId,
        () => false,
      );
      const activated = await current.activation;
      // Release activation now; keep a retry's reload deduplicated until Retry clears.
      if (activated && sameSession() && work.current === current) {
        current.activation = undefined;
        if (!current.retry) work.current = { sessionKey };
        setRetryRequest(previous => previous === current.retry ? undefined : previous);
      }
      if (isCurrent()) report(activated || clerk.organization?.id ? undefined : "activation");
    };

    void initialize();
    return () => { cancelled = true; };
  }, [clerk, clerk.loaded, user, userId, session, sessionId, sessionKey, organization?.id,
    isLoading, count, defaultOrganizationId, error, retry]);

  return {
    error: failure?.sessionKey === sessionKey ? failure.error : undefined,
    retry: () => { setFailure(undefined); setRetryRequest({ sessionKey }); },
  };
}
